import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import { Box, Play, Music, Edit3, Save, X, Image, Type, Clock, Calendar, BarChart3, Cloud, Newspaper, Bell, Sun, Moon } from 'lucide-react';
import { Responsive } from "react-grid-layout";
import { WidthProvider } from "react-grid-layout";
import 'react-resizable/css/styles.css';
import '../styles/SharedStyles.css';
import SummaryHeader from '../components/SummaryHeader';

const ResponsiveGridLayout = WidthProvider(Responsive);

const SummaryDashboard = () => {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [widgets, setWidgets] = useState([]);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const [layout, setLayout] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weather, setWeather] = useState({ temp: 25, condition: 'Sunny', location: 'New York' });
  const [news, setNews] = useState([
    { title: 'Breaking News: Tech Update', summary: 'Latest in technology...', time: '2h ago' },
    { title: 'Market Report', summary: 'Stocks are up...', time: '4h ago' }
  ]);
  const [notifications, setNotifications] = useState([
    { message: 'Server maintenance scheduled', type: 'info', time: '1h ago' },
    { message: 'New user registered', type: 'success', time: '3h ago' }
  ]);

  const fetchWidgets = useCallback(async () => {
    try {
      const data = await api.widgets.getAll();
      setWidgets(data);

      const initialLayout = data.map(w => ({
        i: String(w.id),
        x: w.layoutX || 0,
        y: w.layoutY || 0,
        w: w.layoutW || 2,
        h: w.layoutH || 2
      }));
      setLayout(initialLayout);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchWidgets();
  }, [fetchWidgets]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Mock API calls - in real app, fetch from actual APIs
    const fetchWeather = () => {
      // Simulate API call
      setWeather({
        temp: Math.floor(Math.random() * 30) + 15,
        condition: ['Sunny', 'Cloudy', 'Rainy'][Math.floor(Math.random() * 3)],
        location: 'New York'
      });
    };

    const fetchNews = () => {
      setNews([
        { title: 'Latest Tech News', summary: 'AI advancements continue...', time: '1h ago' },
        { title: 'Business Update', summary: 'Market trends...', time: '2h ago' },
        { title: 'Sports Highlights', summary: 'Championship results...', time: '3h ago' }
      ]);
    };

    const fetchNotifications = () => {
      setNotifications([
        { message: 'System update available', type: 'info', time: '30m ago' },
        { message: 'Backup completed', type: 'success', time: '1h ago' },
        { message: 'High CPU usage detected', type: 'warning', time: '2h ago' }
      ]);
    };

    fetchWeather();
    fetchNews();
    fetchNotifications();

    const interval = setInterval(() => {
      fetchWeather();
      fetchNews();
      fetchNotifications();
    }, 300000); // Update every 5 minutes

    return () => clearInterval(interval);
  }, []);

  const handleLayoutChange = (newLayout) => {
    if (isEditingLayout) {
      setLayout(newLayout);
    }
  };

  const saveLayout = async () => {
    try {
      await api.widgets.updateLayout(layout);
      setIsEditingLayout(false);
    } catch (err) {
      console.error(err);
    }
  };

  const getYoutubeEmbedUrl = (url) => {
    let videoId = '';
    try {
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      } else if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1].split('&')[0];
      } else if (url.includes('youtube.com/embed/')) {
        return url;
      }
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    } catch {
      // Ignore invalid URLs
    }
    return url;
  };

  const getSpotifyEmbedUrl = (url) => {
    if (!url) return '';
    if (url.includes('embed')) return url;
    return url.replace('open.spotify.com/', 'open.spotify.com/embed/');
  };

  const renderWidget = (widget) => {
    switch (widget.type) {
      case 'youtube':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Play size={18} style={{ color: '#ff0000' }} /> {widget.title}
            </h3>
            <div className="flex-1 w-full relative" style={{ minHeight: '0' }}>
              <iframe
                width="100%"
                height="100%"
                src={getYoutubeEmbedUrl(widget.url)}
                title={widget.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ borderRadius: '8px', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
              ></iframe>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      case 'spotify':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Music size={18} style={{ color: '#1db954' }} /> {widget.title}
            </h3>
            <div className="flex-1 w-full">
              <iframe
                src={getSpotifyEmbedUrl(widget.url)}
                width="100%"
                height="100%"
                frameBorder="0"
                allowFullScreen=""
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{ borderRadius: '12px' }}
              ></iframe>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      case 'image':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Image size={20} className="text-primary" /> {widget.title}
            </h3>
            <div className="flex-1 w-full relative" style={{ minHeight: '0' }}>
              <img
                src={widget.url}
                alt={widget.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
              />
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      case 'text':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Type size={20} className="text-primary" /> {widget.title}
            </h3>
            <div className="flex-1 w-full p-4 bg-surface rounded-lg overflow-auto">
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{widget.url}</div>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      case 'clock':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Clock size={20} className="text-primary" /> {widget.title}
            </h3>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl font-mono font-bold text-primary">
                  {currentTime.toLocaleTimeString()}
                </div>
                <div className="text-lg text-muted mt-2">
                  {currentTime.toLocaleDateString()}
                </div>
              </div>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      case 'calendar':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Calendar size={20} className="text-primary" /> {widget.title}
            </h3>
            <div className="flex-1 p-4">
              <div className="text-center">
                <div className="text-2xl font-bold mb-2">
                  {currentTime.toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                </div>
                <div className="grid grid-cols-7 gap-1 text-sm">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="font-semibold text-muted p-1">{day}</div>
                  ))}
                  {Array.from({ length: 35 }, (_, i) => {
                    const date = new Date(currentTime.getFullYear(), currentTime.getMonth(), i - currentTime.getDay() + 1);
                    const isCurrentMonth = date.getMonth() === currentTime.getMonth();
                    const isToday = date.toDateString() === new Date().toDateString();
                    return (
                      <div
                        key={i}
                        className={`p-1 rounded ${isToday ? 'bg-primary text-white' : isCurrentMonth ? 'text-foreground' : 'text-muted'}`}
                      >
                        {date.getDate()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      case 'stats':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <BarChart3 size={20} className="text-primary" /> {widget.title}
            </h3>
            <div className="flex-1 p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">42</div>
                  <div className="text-sm text-muted">Active Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-success">128</div>
                  <div className="text-sm text-muted">Total Servers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-warning">15</div>
                  <div className="text-sm text-muted">Widgets</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-danger">99.9%</div>
                  <div className="text-sm text-muted">Uptime</div>
                </div>
              </div>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      case 'weather':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Cloud size={20} className="text-primary" /> {widget.title}
            </h3>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-2">
                  {weather.condition === 'Sunny' ? '☀️' : weather.condition === 'Cloudy' ? '☁️' : '🌧️'}
                </div>
                <div className="text-3xl font-bold text-primary">{weather.temp}°C</div>
                <div className="text-lg text-muted">{weather.condition}</div>
                <div className="text-sm text-muted mt-1">{weather.location}</div>
              </div>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      case 'news':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Newspaper size={20} className="text-primary" /> {widget.title}
            </h3>
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-3">
                {news.map((item, index) => (
                  <div key={index} className="p-3 bg-surface rounded-lg border">
                    <h4 className="text-sm font-semibold mb-1">{item.title}</h4>
                    <p className="text-xs text-muted mb-1">{item.summary}</p>
                    <span className="text-xs text-muted">{item.time}</span>
                  </div>
                ))}
              </div>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      case 'notifications':
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Bell size={20} className="text-primary" /> {widget.title}
            </h3>
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-2">
                {notifications.map((notif, index) => (
                  <div key={index} className={`p-3 rounded-lg border-l-4 ${notif.type === 'success' ? 'border-l-success bg-success/10' :
                      notif.type === 'warning' ? 'border-l-warning bg-warning/10' :
                        'border-l-primary bg-primary/10'
                    }`}>
                    <p className="text-sm">{notif.message}</p>
                    <span className="text-xs text-muted">{notif.time}</span>
                  </div>
                ))}
              </div>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
      default: // app or container
        return (
          <>
            <h3 className="mb-2 flex items-center gap-2">
              <Box size={20} className="text-primary" /> {widget.title}
            </h3>
            <div className="flex-1 w-full relative" style={{ minHeight: '0' }}>
              <iframe
                src={widget.url}
                width="100%"
                height="100%"
                frameBorder="0"
                allowFullScreen
                style={{ borderRadius: '8px', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                title={widget.title}
              ></iframe>
            </div>
            {widget.description && <p className="text-muted mt-2 text-sm">{widget.description}</p>}
          </>
        );
    }
  };

  return (
    <div className={`shared-page ${theme}`}>
      <div className="glow-effect glow-1"></div>
      <div className="glow-effect glow-2"></div>
      <div className="container shared-content" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
      <SummaryHeader />

      <div className="flex justify-between items-center" style={{ marginBottom: '2rem' }}>
        <div className="shared-section-header" style={{ textAlign: 'left', marginBottom: 0 }}>
          <h2 style={{ justifyContent: 'flex-start' }}>Dashboard</h2>
          <p>{t('dragResizeHint')}</p>
        </div>
        {isAdmin && widgets.length > 0 && (
          <div className="flex gap-2">
            {isEditingLayout ? (
              <>
                <button className="btn btn-outline flex items-center gap-2 hover-lift" onClick={() => { setIsEditingLayout(false); fetchWidgets(); }}>
                  <X size={16} /> {t('cancel')}
                </button>
                <button className="btn btn-primary flex items-center gap-2 hover-lift" onClick={saveLayout}>
                  <Save size={16} /> {t('save')}
                </button>
              </>
            ) : (
              <button className="btn btn-outline flex items-center gap-2 hover-lift" onClick={() => setIsEditingLayout(true)}>
                <Edit3 size={16} /> {t('editLayout')}
              </button>
            )}
          </div>
        )}
      </div>

      {widgets.length === 0 ? (
        <div className="shared-empty">
          <Box size={48} />
          <p>{t('noWidgets')}</p>
          <p style={{ fontSize: '0.875rem' }}>{t('widgetsPageSubtitle')}</p>
        </div>
      ) : (
        <div className="grid-layout-container">
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: layout }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={80}
            onLayoutChange={handleLayoutChange}
            isDraggable={isEditingLayout}
            isResizable={isEditingLayout}
            margin={[24, 24]}
            containerPadding={[0, 0]}
            draggableHandle=".drag-handle"
          >
            {widgets.map(w => (
              <div
                key={String(w.id)}
                className={`server-card flex flex-col ${isEditingLayout ? 'editing' : ''} hover-lift`}
                style={{ overflow: 'hidden', transition: 'all 0.2s ease' }}
              >
                {isEditingLayout && (
                  <div className="drag-handle" style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, cursor: 'move', background: 'rgba(56, 189, 248, 0.1)', padding: '4px', borderRadius: '8px' }}>
                    <Edit3 size={16} className="text-primary" />
                  </div>
                )}
                {renderWidget(w)}
              </div>
            ))}
          </ResponsiveGridLayout>
        </div>
      )}
      </div>
    </div>
  );
};

export default SummaryDashboard;
